<script lang="ts" setup>
import { useDefaultAvatar, usePreviewImg } from "@/use/common";
import {
  Image,
  showConfirmDialog,
  showFailToast,
  showLoadingToast,
  showSuccessToast,
} from "vant";
import { Media, Story } from "@/types/entity";
import { getDisplayDateTime, isImage } from "@/modules/common";
import PlayIcon from "@/components/icon/playIcon.vue";
import { computed, nextTick, ref } from "vue";
import { useMate } from "@/stores/mate";
import { useRouter } from "vue-router";
import { useStory } from "@/stores/story";
import {
  extractImgAndTxtList,
  getWordCount,
  text2GameStory,
} from "@/modules/editor/editor-json-utils";
import ReactionCounter from "@/components/mobile/reply/ReactionCounter.vue";
import HeartIcon from "@/components/icon/HeartIcon.vue";
import AIcon from "@/components/icon/AIcon.vue";
import EyeIcon from "@/components/icon/EyeIcon.vue";
import i18n from "@/locales";
import { ApiPostStoryGameDialogCreateMany } from "@/apis/story-game";
import { StoryScenePreviewPopupInstance } from "../../generation/CreateStorySceneTaskPreview.vue";
import { GAL_GAME_URL_PREFIX } from "@/stores/storyGameEditor";
import api from "@/modules/api";
// Props & Emit
const props = defineProps<{
  story: Story;
  preItem?: Story;
  nextItem?: Story;
}>();
const emit = defineEmits<{
  (e: "onMediaPreview", idx: number, urlList: string[]): void;
  (e: "onShareClick", story: Story, isReset?: boolean, isVideo?: boolean): void;
  (e: "onPublishClick", story: Story): void;
  (e: "onEditClick", story: Story): void;
}>();
// State
const router = useRouter();
const mate = useMate();
const storyStore = useStory();
const isOwner = computed(() => {
  return props.story.mate.id === mate.userMate.id;
});
const wordCount = computed(() => {
  const json = storyStore.getJson(props.story.text);
  return getWordCount(json);
});
const reactionCount = computed(() => {
  return props.story.reply.interactivityEmoji.reduce(
    (acc, cur) => acc + cur.amount,
    0
  );
});
const isGenerating = computed(() => {
  return !props.story.text;
});
type SimpleMedia = Pick<Media, "url" | "videoCoverUrl"> & { text?: string };
const mediaList = computed<SimpleMedia[]>(() => {
  const json = storyStore.getJson(props.story.text);
  const picList: SimpleMedia[] = extractImgAndTxtList(json);
  if (!props.story.videoMedia) {
    return picList;
  } else {
    return [
      {
        url: props.story.videoMedia.url,
        videoCoverUrl: props.story.videoMedia.videoCoverUrl,
      },
      ...picList,
    ] as SimpleMedia[];
  }
});
// Event Handler
const onReadClick = () => {
  router.push({
    name: "readStory",
    params: { uuid: props.story!.uuid },
  });
};
const clickDelete = async () => {
  try {
    await showConfirmDialog({
      title: i18n.global.t("ConfirmModal.deleteChapter"),
      message: i18n.global.t("ConfirmModal.deleteCurrentChatInfo"),
      confirmButtonText: i18n.global.t("Buttons.confirm"),
      cancelButtonText: i18n.global.t("Buttons.cancel"),
    });
    const loadingToast = showLoadingToast({
      message: i18n.global.t("Message.deletingChapter"),
      duration: 0,
    });
    try {
      await storyStore.deleteStory(props.story!);
      loadingToast.close();
      showSuccessToast(i18n.global.t("Message.deleteChapterSuccessful"));
    } finally {
      loadingToast.close();
    }
  } catch {
    // Cancel => Do nothing
  }
};
const onCardClick = () => {
  if (isGenerating.value) return;
  onReadClick();
};

// Action Popup
const showActions = ref(false);
const showEmoji = ref(0);
const toOperateReply = ref<Reply | null>(null);

const onReplyActionClick = (reply: Reply) => {
  toOperateReply.value = reply;
  showActions.value = true;
};
const clickGenerateStoryScene = async (story: Story) => {
  // 重新生成
  const loading = showLoadingToast({
    message: i18n.global.t("Message.creatingStorySceneTask"),
  });

  let { content: _story } = JSON.parse(story.text) || {};
  if (!_story) return loading.close();
  const list = text2GameStory(story.id!, _story);
  if (list.length === 0) {
    showFailToast(i18n.global.t("Message.storyContentCanNotEmpty"));
    return loading.close();
  }

  const { data } = await ApiPostStoryGameDialogCreateMany(list);
  const { id, storyGameId, storyGame } = data;
  story.storyGameId = storyGameId;
  story.storyGameUuid = storyGame.uuid;
  story.storyGameStoryNodeId = id;

  loading.close();
  router.push({
    name: "storySceneGeneration",
    query: {
      storyGameUuid: storyGame.uuid,
      storyUuid: story!.uuid,
      storyNodeId: id,
      storyGameId,
    },
  });
};

const previewPopupRef = ref<StoryScenePreviewPopupInstance | null>(null);
const selectedStory = ref<Story | null>(null);
const clickViewStoryScene = (story: Story) => {
  selectedStory.value = story;
  nextTick(() => {
    previewPopupRef.value?.startPreview({
      nodeId: story.storyGameStoryNodeId,
    });
  });
};

const clickImmersiveRead = (story: Story) => {
  window.open(
    GAL_GAME_URL_PREFIX +
      `?uuid=${story.storyGameUuid}&nodeId=${story.storyGameStoryNodeId}&type=story`
  );
};

const clickSort = async (swapItem: Story) => {
  const { id, order } = props.story;
  const loading = showLoadingToast({
    message: i18n.global.t("Message.loading"),
  });
  await api.putStorySort([
    { id: id!, order: swapItem.order! },
    { id: swapItem.id!, order: order! },
  ]);
  props.story.order = swapItem.order;
  swapItem.order = order;
  loading.close();
};
</script>
<template>
  <div
    :class="{ 'opacity-50': isGenerating }"
    class="flex flex-col bg-base-100 rounded-lg overflow-hidden"
  >
    <!-- 基本信息 -->
    <div class="flex flex-col gap-2 p-3" @click="onCardClick">
      <div class="text-xl font-bold overflow-hidden flex items-center gap-1.5">
        <span class="line-clamp-1">
          {{
            !isGenerating
              ? story.title
              : i18n.global.t("story.chapterGeneration")
          }}
        </span>

        <div
          class="py-1 px-2.5 rounded text-sm font-normal whitespace-nowrap badge"
        >
          {{ $t(`Visibilities.status.${story.visibility}`) }}
        </div>

        <div
          v-if="isGenerating || !story.dtPublish"
          class="py-1 px-2.5 rounded text-sm font-normal whitespace-nowrap badge"
        >
          {{ isGenerating ? $t("story.processing") : $t("story.draft") }}
        </div>
        <button
          v-if="!story.dtPublish"
          class="btn btn-sm rounded btn-primary"
          @click.stop="emit('onPublishClick', story)"
        >
          {{ $t("Buttons.publish") }}
        </button>
      </div>
      <div class="flex gap-4 relative">
        <div class="relative flex flex-col justify-center items-center gap-1.5">
          <Image
            :src="
              story.media?.url
                ? usePreviewImg(story.media.url)
                : useDefaultAvatar()
            "
            alt=""
            class="h-[130px] aspect-[1/1] object-cover shrink-0 rounded overflow-hidden shadow bg-primary"
            fit="cover"
            crossorigin="anonymous"
          />
          <div class="flex text-base-content/50 w-full justify-between px-5">
            <div class="flex flex-col items-center justify-center gap-0.5">
              <div>{{ story.reply?.viewerCount || 0 }}</div>
              <eye-icon class="icon-sm" />
            </div>
            <div class="flex flex-col items-center justify-center gap-0.5">
              <div>{{ wordCount }}</div>
              <a-icon class="icon-sm" />
            </div>
            <div class="flex flex-col items-center justify-center gap-0.5">
              <div>{{ reactionCount }}</div>
              <heart-icon class="icon-sm" />
            </div>
          </div>
        </div>
        <div
          class="flex flex-col gap-1 flex-grow overflow-hidden text-base-content/50"
        >
          <p class="line-clamp-5 h-[100px]">
            {{ isGenerating ? $t("story.waitTimeMinutes") : story.description }}
          </p>
          <template v-if="isOwner">
            <div class="flex justify-end whitespace-nowrap text-xs mt-1 gap-1">
              <template v-if="isGenerating">
                <p>{{ $t("story.generateTime") }}</p>
                <p>{{ getDisplayDateTime(story?.dtCreate!) }}</p>
              </template>
              <template v-else-if="story.dtPublish">
                <p>{{ $t("story.publishedOn") }}</p>
                <p>{{ getDisplayDateTime(story.dtPublish) }}</p>
              </template>
              <template v-else>
                <p>{{ $t("story.editOn") }}</p>
                <p>{{ getDisplayDateTime(story?.dtUpdate!) }}</p>
              </template>
            </div>
            <div class="flex items-center justify-end gap-2 mt-2">
              <!-- <button
                class="btn btn-sm rounded"
                @click.stop="
                  router.push({
                    name: 'storyImgModule',
                    query: { story: story.uuid },
                  })
                "
              >
                <i class="iconfont icon-story font-light"></i>
                <span class="font-normal">{{ $t(`story.insertImgMode`) }}</span>
              </button> -->
              <button
                class="btn btn-sm rounded"
                @click.stop="
                  router.push({
                    name: 'storyEditor',
                    params: { uuid: story!.uuid },
                  })
                "
              >
                <i class="iconify mingcute--edit-3-line font-light"></i>
                <span class="font-normal">{{ $t(`story.body`) }}</span>
              </button>
            </div>
          </template>
          <template v-else>
            <div class="flex items-center justify-end gap-2 mt-8">
              <button
                :class="[
                  'btn btn-sm  rounded',
                  story.storyGameStoryNodeId ? '' : 'btn-primary',
                ]"
              >
                <span class="font-normal">{{ $t(`story.imgTextRead`) }}</span>
              </button>
              <button
                v-if="story.storyGameStoryNodeId"
                class="btn btn-sm btn-primary rounded"
                @click.stop="clickImmersiveRead(story)"
              >
                <span class="font-normal">{{ $t(`story.immersiveRead`) }}</span>
              </button>
            </div>
          </template>
        </div>
      </div>
      <!--  未发布 & 所有者  -->
      <div v-if="isOwner" class="flex gap-2 items-center justify-between">
        <div class="flex flex-col gap-2">
          <!-- 图文 -->
          <!-- <div class="flex items-center text-base-content/50">
            <i class="iconify mingcute--pic-line mr-1" />
            <span class="text-base">{{ $t(`story.imgText`) }}</span>
            <span
              :class="[
                'mx-2.5',
                story.screenshotMediaId ? ' text-[#00DA39]' : 'text-[#FED500]',
              ]"
            >
              {{
                $t(
                  `story.${
                    story.screenshotMediaId ? "generated" : "notGenerate"
                  }`
                )
              }}
            </span>
            <button
              v-if="!story.screenshotMediaId"
              class="btn btn-sm rounded"
              @click.stop="emit('onShareClick', story, true, false)"
            >
              <span class="font-normal">{{ $t(`Buttons.generate`) }}</span>
            </button>
            <template v-else>
              <button
                class="btn btn-sm rounded mr-2.5"
                @click.stop="emit('onShareClick', story, false, false)"
              >
                <span class="font-normal">{{ $t(`Buttons.view`) }}</span>
              </button>
              <button
                class="btn btn-sm rounded"
                @click.stop="emit('onShareClick', story, true, false)"
              >
                <span class="font-normal">{{ $t(`Buttons.regenerate`) }}</span>
              </button>
            </template>
          </div> -->
          <!-- 视频 -->
          <!-- <div class="flex items-center text-base-content/50">
            <i class="iconfont icon-play mr-1" />
            <span class="text-base">{{ $t(`story.video`) }}</span>
            <span
              :class="[
                'mx-2.5',
                story.videoMedia ? ' text-[#00DA39]' : 'text-[#FED500]',
              ]"
            >
              {{
                $t(`story.${story.videoMedia ? "generated" : "notGenerate"}`)
              }}
            </span>
            <button
              v-if="!story.videoMedia"
              class="btn btn-sm  rounded"
              @click.stop="
                router.push({
                  name: 'videoGeneration',
                  query: {
                    storyUuid: story!.uuid,
                  },
                })
              "
            >
              <span class="font-normal"> {{ $t(`Buttons.generate`) }} </span>
            </button>

            <template v-else>
              <button
                class="btn btn-sm  rounded mr-2.5"
                @click.stop="emit('onShareClick', story, false, true)"
              >
                <span class="font-normal">{{ $t(`Buttons.view`) }}</span>
              </button>
              <button
                class="btn btn-sm  rounded"
                @click.stop="
                  router.push({
                    name: 'videoGeneration',
                    query: {
                      storyUuid: story!.uuid,
                    },
                  })
                "
              >
                <span class="font-normal">
                  {{ $t(`Buttons.regenerate`) }}
                </span>
              </button>
            </template>
          </div> -->
          <!-- 视觉小说 -->
          <!-- <div class="flex items-center text-base-content/50">
            <div>
              <i class="iconfont icon-script-game mr-1" />
              <span class="text-base">{{ $t(`story.visualNovel`) }}</span>
            </div>
            <span
              :class="[
                'mx-2.5',
                story.storyGameStoryNodeId
                  ? ' text-[#00DA39]'
                  : 'text-[#FED500]',
              ]"
            >
              {{
                $t(
                  `story.${
                    story.storyGameStoryNodeId ? "generated" : "notGenerate"
                  }`
                )
              }}
            </span>
            <button
              v-if="!story.storyGameStoryNodeId"
              class="flex-1 btn btn-sm rounded"
              @click.stop="clickGenerateStoryScene(story)"
            >
              <span class="font-normal">
                {{ $t(`Buttons.generate`) }}
              </span>
            </button>

            <div v-else class="flex-1 flex items-center flex-wrap gap-2">
              <button
                class="btn btn-sm rounded"
                @click.stop="clickViewStoryScene(story)"
              >
                <span class="font-normal">{{ $t(`Buttons.view`) }}</span>
              </button>
              <button
                class="btn btn-sm rounded"
                @click.stop="
                  router.push({
                    name: 'storySceneGeneration',
                    query: {
                      storyUuid: story!.uuid,
                      storyNodeId: story.storyGameStoryNodeId,
                      storyGameId: story.storyGameId,
                      storyGameUuid: story.storyGameUuid,
                    },
                  })
                "
              >
                <span class="font-normal">{{ $t(`Buttons.edit`) }}</span>
              </button>
              <button
                class="btn btn-sm rounded"
                @click.stop="clickGenerateStoryScene(story)"
              >
                <span class="font-normal">
                  {{ $t(`Buttons.regenerate`) }}
                </span>
              </button>
            </div>
          </div> -->
        </div>
      </div>
    </div>
    <div v-if="isOwner" class="flex justify-between p-2 gap-3">
      <div class="flex-1">
        <button
          class="btn btn-sm rounded w-full"
          @click.stop="emit(`onEditClick`, story)"
        >
          <i class="iconify mingcute--edit-3-line" />
          <span class="font-normal">
            {{ $t(`story.chapterInfo`) }}
          </span>
        </button>
      </div>
      <div class="flex gap-2">
        <button
          v-if="preItem"
          class="btn btn-sm rounded"
          @click="clickSort(preItem)"
        >
          <i class="iconfont icon-up" />
        </button>

        <button
          v-if="nextItem"
          class="btn btn-sm rounded"
          @click="clickSort(nextItem)"
        >
          <i class="iconfont icon-down" />
        </button>

        <button class="btn btn-sm rounded" @click="clickDelete">
          <i class="iconfont icon-delete" />
        </button>
      </div>
    </div>
    <!-- Chapter Gallery -->
    <div
      v-if="!isOwner && mediaList.length > 0"
      class="story-book-gallery-overview w-full overflow-x-auto flex flex-nowrap gap-1 bg-base-200"
    >
      <div
        v-for="(item, idx) in mediaList"
        :key="idx"
        class="w-28 aspect-[3/4] shrink-0 overflow-hidden"
        @click="
          emit(
            'onMediaPreview',
            idx,
            mediaList.map((m) => m.url)
          )
        "
      >
        <div v-if="isImage(item.url)" class="w-full h-full relative">
          <BaseImg
            :media="item"
            class="w-full h-full"
            fit="cover"
            ratio="3:4"
          />
        </div>
        <div v-else class="w-full h-full relative">
          <Image
            :src="item.videoCoverUrl"
            class="w-full h-full"
            fit="cover"
            crossorigin="anonymous"
          />
          <div
            class="absolute top-0 left-0 right-0 bottom-0 flex justify-center items-center"
          >
            <play-icon class="w-10 h-10" />
          </div>
        </div>
      </div>
    </div>
    <!-- 交互区 -->

    <div v-if="!isOwner" class="flex flex-col gap-2 px-5 py-2 border-b">
      <reaction-mate :reply="story.reply" />
      <div class="flex w-full justify-between items-end">
        <div class="flex-1 flex gap-2 flex-wrap">
          <!-- @vue-ignore -->
          <!-- <Donate :item="story" type="Story" />
          <coin-vote :item="story" :type="CoinBalanceRecordEntityType.Story" />
          <button
            class="btn btn-sm rounded"
            @click="showEmoji++"
          >
            <i class="iconify mingcute--thumb-up-line" />
          </button> -->
          <button
            class="btn btn-sm rounded"
            @click="()=>router.push({
              name: 'storyReply',
              params: { uuid: story!.uuid },
            })"
          >
            {{ $t("Overview.comments") }}
            <span v-if="story.reply && story.reply.replyCount > 0">
              ({{ story.reply?.replyCount }})
            </span>
          </button>
          <reaction-counter :reply="story.reply" />
        </div>
      </div>
    </div>
    <moment-actions
      v-model:show="showActions"
      :reply="story.reply"
      :showEmoji="showEmoji"
    />
    <!-- <div class="flex p-3 justify-between">
      <div class="flex gap-4">
        <div v-if="story.dtPublish" class="flex gap-1 items-center">
          <button
            class="btn btn-xs  text-base-content/50 rounded"
            @click="
              router.push({
                name: 'storyReply',
                params: { uuid: story!.uuid },
              })
            "
          >
            {{ $t("Overview.comments") }}
            {{
              story.reply.replyCount > 0 ? `(${story.reply.replyCount})` : ""
            }}
          </button>
          <reaction-counter :reply="story.reply" />
        </div>
      </div>

      <div
        v-if="!story.dtPublish"
        class="flex flex-col items-end justify-center text-base-content/50"
      >
        <div>{{ $t("Overview.updateTime") }}</div>
        {{ getDisplayDateTime(story.dtUpdate) }}
      </div>
    </div> -->
  </div>

  <CreateStorySceneTaskPreview
    v-if="selectedStory"
    :id="selectedStory.storyGameId!"
    ref="previewPopupRef"
    :story="selectedStory"
    :uuid="selectedStory.storyGameUuid!"
  />
</template>
